import { ReactNode } from 'react';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, ForwardTotalTooltip, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import type { StrategyPresentation } from './types';

function acioExponentLabel(capYears: number): ReactNode {
  return capYears > 0 ? <>min(policy year - 1, {capYears})</> : <>policy year - 1</>;
}

function assetFlexGrowthTooltip({
  header,
  description,
  startingLabel,
  startingValue,
  rateLabel,
  rate,
  capYears,
  policyYear,
  result,
}: {
  header: string;
  description: string;
  startingLabel: string;
  startingValue: number;
  rateLabel: string;
  rate: number;
  capYears: number;
  policyYear: number;
  result: number;
}): ReactNode {
  const ratePct = (rate * 100).toFixed(2);
  const rawIncrements = Math.max(Math.floor(policyYear) - 1, 0);
  const increments = capYears > 0 ? Math.min(rawIncrements, capYears) : rawIncrements;
  const capped = capYears > 0 && rawIncrements > capYears;

  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{header}</div>
      <div className="mb-2 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">{rateLabel}</span>
        <span className="font-semibold tabular-nums">{ratePct}%</span>
        <span className="text-gray-300">Policy-year increases</span>
        <span className="font-semibold tabular-nums">{increments}</span>
        <span className="text-gray-300">Formula</span>
        <span className="font-semibold tabular-nums">
          {currency(startingValue)} × (1 + {ratePct}%)<sup>{increments}</sup>
        </span>
        <span className="text-gray-300">Displayed Year value</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        {capped && (
          <>
            <span />
            <span className="text-[10px] text-amber-300">
              ACIO capped after {capYears} annual increases.
            </span>
          </>
        )}
      </div>
    </TooltipContent>
  );
}

function assetFlexStaticTooltip({
  header,
  description,
  inputLabel,
  inputValue,
  result,
}: {
  header: string;
  description: string;
  inputLabel: string;
  inputValue: number;
  result: number;
}): ReactNode {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">{header}</div>
      <div className="mb-2 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{inputLabel}</span>
        <span className="font-semibold tabular-nums">{currency(inputValue)}</span>
        <span className="text-gray-300">Formula</span>
        <span className="font-semibold tabular-nums">{inputLabel}</span>
        <span className="text-gray-300">Displayed Year value</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
      </div>
    </TooltipContent>
  );
}

// Asset Flex accumulation: a two-pool product whose Acceleration (AOB) and
// Extension of Benefits (EOB) pools carry independent ACIO rates. The universal
// default would show a single combined "LTC pool" with one rate — wrong (and no
// tooltip at all when the On-Face rate is 0 but EOB still grows). So we split
// the pools and give each a tooltip on its own rate. The per-pool inflated
// ceilings come straight from the snapshot: EOB = effectiveContinuationBenefit;
// Acceleration = theoreticalLtcPoolCeiling − that.
function buildAssetFlexAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const baseRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const baseCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const eobRate = terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate;
  const eobCapYears = terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears;

  const accel = (s: YearSnapshot) => s.theoreticalLtcPoolCeiling - s.effectiveContinuationBenefit;
  const eob = (s: YearSnapshot) => s.effectiveContinuationBenefit;
  // The EOB monthly cap grows on the On-COB ACIO, which can differ from the
  // On-Face ACIO driving the Acceleration monthly cap. When the two diverge
  // (e.g. a level-AOB / inflating-EOB illustration), surface a dedicated EOB
  // monthly-benefit column so the EOB pool's growth has a visible driver —
  // mirroring the carrier's two "Monthly Benefit for LTC / EOB" columns.
  const eobMonthly = (s: YearSnapshot) => s.continuationMonthlyBenefitCap ?? s.monthlyBenefitCap;
  const showEobMonthly = timeline.some(
    r => Math.abs(eobMonthly(r.snapshot) - r.snapshot.monthlyBenefitCap) > 0.5,
  );

  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (timeline.some(r => accel(r.snapshot) > 0.5)) {
    columns.push({
      key: 'acceleration-pool',
      label: 'Acceleration (AOB)',
      tooltip: (
        <HeaderTooltip
          description="Shows the Acceleration Benefit (AOB) dollars available in each Year before care begins."
          formulas={[
            {
              label: 'Year value',
              expression: (
                <>
                  AOB at issue ×{' '}
                  <GrowthFactor
                    rate="On-Face ACIO"
                    exponent={acioExponentLabel(baseCapYears)}
                  />
                </>
              ),
            },
          ]}
          notes="Displayed output: this Year's AOB portion of the LTC benefit pool."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={accel(row.snapshot)}
          prevValue={prev ? accel(prev.snapshot) : null}
          tooltip={
            terms.baseBenefitPoolAtIssue != null
              ? assetFlexGrowthTooltip({
                  header: `Year ${row.policyYear} Acceleration (AOB)`,
                  description: 'Shows AOB dollars available before care begins.',
                  startingLabel: `AOB at issue (${terms.policyPremiumStartYear})`,
                  startingValue: terms.baseBenefitPoolAtIssue,
                  rateLabel: 'On-Face ACIO',
                  rate: baseRate,
                  capYears: baseCapYears,
                  policyYear: row.policyYear,
                  result: accel(row.snapshot),
                })
              : undefined
          }
        />
      ),
    });
  }

  if (timeline.some(r => eob(r.snapshot) > 0.5)) {
    columns.push({
      key: 'eob-pool',
      label: 'Extension (EOB)',
      tooltip: (
        <HeaderTooltip
          description="Shows the Extension of Benefits (EOB) dollars available in each Year before care begins. The EOB pool grows because the EOB monthly benefit grows (pool = EOB monthly cap × EOB duration months)."
          formulas={[
            {
              label: 'Year value',
              expression: (
                <>
                  EOB at issue ×{' '}
                  <GrowthFactor
                    rate="On-COB ACIO"
                    exponent={acioExponentLabel(eobCapYears)}
                  />
                </>
              ),
            },
          ]}
          notes="Displayed output: this Year's EOB portion of the LTC benefit pool — equivalently, the EOB monthly cap inflated by On-COB ACIO times the EOB duration months."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={eob(row.snapshot)}
          prevValue={prev ? eob(prev.snapshot) : null}
          tooltip={
            terms.extensionOfBenefitsPoolAtIssue != null
              ? assetFlexGrowthTooltip({
                  header: `Year ${row.policyYear} Extension (EOB)`,
                  description:
                    'EOB dollars before care begins. Grows with the EOB monthly benefit (cap × duration months), which inflates on the On-COB ACIO.',
                  startingLabel: `EOB at issue (${terms.policyPremiumStartYear})`,
                  startingValue: terms.extensionOfBenefitsPoolAtIssue,
                  rateLabel: 'On-COB ACIO',
                  rate: eobRate,
                  capYears: eobCapYears,
                  policyYear: row.policyYear,
                  result: eob(row.snapshot),
                })
              : undefined
          }
        />
      ),
    });
  }

  // Forward-summed Total LTC Benefit — only under the duration model (the
  // snapshot carries `forwardLtcBenefitTotal`). Distinct from the Acceleration /
  // EOB pool columns above: those are the principal a claim opens with this
  // year, while this is the total paid over the whole benefit period as the
  // monthly benefit keeps inflating during the claim. The two converge at the
  // ACIO cap year.
  if (timeline.some(r => (r.snapshot.forwardLtcBenefitTotal ?? 0) > 0.5)) {
    columns.push({
      key: 'total-ltc-benefit',
      label: 'Total LTC benefit',
      tooltip: (
        <HeaderTooltip
          description="Total long-term care dollars the policy would pay for a claim STARTING this policy year, summed across the full Acceleration (AOB) then Extension (EOB) benefit period as the monthly benefit keeps inflating during the claim. This mirrors the carrier illustration's forward-inflated total."
          formulas={[
            {
              label: 'Total LTC benefit',
              expression:
                'Σ (monthly benefit × ACIO) over the AOB then EOB benefit-period months',
            },
          ]}
          notes="Exceeds the current Acceleration + EOB pool because the monthly benefit keeps growing with ACIO while the claim is being paid; the two converge once ACIO reaches its cap year."
        />
      ),
      render: (row, prev) => {
        const value = row.snapshot.forwardLtcBenefitTotal ?? 0;
        return (
          <GrowthValueCell
            value={value}
            prevValue={prev?.snapshot.forwardLtcBenefitTotal ?? null}
            tooltip={ForwardTotalTooltip({
              policyYear: row.policyYear,
              principal: row.snapshot.theoreticalLtcPoolCeiling,
              total: value,
              headingPrefix: 'Year',
              description: (
                <>
                  Total dollars the policy pays for a claim starting this year, across the full
                  Acceleration (AOB) + Extension (EOB) benefit period.
                </>
              ),
              poolLabel: 'AOB + EOB',
            })}
          />
        );
      },
    });
  }

  if (timeline.some(r => r.snapshot.monthlyBenefitCap > 0.5)) {
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    columns.push({
      key: 'monthly-benefit',
      label: showEobMonthly ? 'Monthly benefit (AOB)' : 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Shows the Acceleration monthly benefit cap for each Year before care begins."
          formulas={[
            {
              label: 'Year value',
              expression: (
                <>
                  at-issue monthly benefit ×{' '}
                  <GrowthFactor
                    rate="On-Face ACIO"
                    exponent={acioExponentLabel(baseCapYears)}
                  />
                </>
              ),
            },
          ]}
          notes="Displayed output: this Year's monthly cap for the Acceleration Benefit."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={assetFlexGrowthTooltip({
            header: `Year ${row.policyYear} monthly benefit`,
            description: 'Shows the Acceleration monthly benefit cap before care begins.',
            startingLabel: `At-issue monthly benefit (${terms.policyPremiumStartYear})`,
            startingValue: baseMonthly,
            rateLabel: 'On-Face ACIO',
            rate: baseRate,
            capYears: baseCapYears,
            policyYear: row.policyYear,
            result: row.snapshot.monthlyBenefitCap,
          })}
        />
      ),
    });
  }

  if (showEobMonthly) {
    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    columns.push({
      key: 'monthly-benefit-eob',
      label: 'Monthly benefit (EOB)',
      tooltip: (
        <HeaderTooltip
          description="Shows the Extension of Benefits (EOB) monthly benefit cap for each Year before care begins. This is the driver of EOB pool growth — the EOB pool is this cap × the EOB duration months."
          formulas={[
            {
              label: 'Year value',
              expression: (
                <>
                  at-issue monthly benefit ×{' '}
                  <GrowthFactor
                    rate="On-COB ACIO"
                    exponent={acioExponentLabel(eobCapYears)}
                  />
                </>
              ),
            },
          ]}
          notes="Displayed output: this Year's monthly cap for the Extension of Benefits. On Asset Flex the EOB cap can inflate even when the Acceleration cap is level."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={eobMonthly(row.snapshot)}
          prevValue={prev ? eobMonthly(prev.snapshot) : null}
          tooltip={assetFlexGrowthTooltip({
            header: `Year ${row.policyYear} EOB monthly benefit`,
            description: 'Shows the Extension of Benefits monthly cap before care begins.',
            startingLabel: `At-issue monthly benefit (${terms.policyPremiumStartYear})`,
            startingValue: baseMonthly,
            rateLabel: 'On-COB ACIO',
            rate: eobRate,
            capYears: eobCapYears,
            policyYear: row.policyYear,
            result: eobMonthly(row.snapshot),
          })}
        />
      ),
    });
  }

  if (timeline.some(r => r.snapshot.effectiveFaceAmount > 0.5)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Shows the face amount carried in each Year before care begins."
          formulas={[
            {
              label: 'Year value',
              expression: 'policy maximum benefit amount',
            },
          ]}
          notes="Displayed output: this Year's death benefit value; no ACIO is applied to this Year value."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={assetFlexStaticTooltip({
            header: `Year ${row.policyYear} death benefit`,
            description: 'Shows the face amount carried before care begins.',
            inputLabel: 'Policy maximum benefit amount',
            inputValue: row.snapshot.effectiveFaceAmount,
            result: row.snapshot.effectiveFaceAmount,
          })}
        />
      ),
    });
  }

  return columns;
}

// NYL Asset Flex presentation: standardizes on the shared
// BalanceBreakdownTooltip for pool / death benefit cells; adds a
// reimbursement-rule explanation on the payout cell.

// Renders a static "$Y/mo × Z mo = $X" note for a duration-derived pool.
// Returns undefined for legacy policies where duration is not known.
function poolAtIssueNote(
  baseMonthly: number,
  durationMonths: number | undefined,
  atIssue: number | undefined,
): string | undefined {
  if (durationMonths === undefined || atIssue === undefined) return undefined;
  return `At issue: ${currency(baseMonthly)}/mo × ${durationMonths} mo = ${currency(atIssue)}`;
}

// Footer for the benefit-amount inflation tooltip: links this year's monthly
// cap to the inflated pool ceilings (cap × duration months) so advisors can
// see where the Acceleration / EOB headline dollars come from. Each pool's
// ceiling uses its OWN monthly cap: the Acceleration cap grows on the On-Face
// ACIO and the EOB cap on the On-COB ACIO (mirroring the engine's per-pool
// caps), so the row's applied cap is only reused for the pool that funded the
// row — the other pool's cap is reconstructed from its own rate/cap-years
// (P2-14). Only shown for duration-derived policies (legacy policies have no
// month count to multiply).
function poolCeilingFooter(month: MonthlyEntry, terms: ClaimTerms): ReactNode | null {
  const accelMonths = terms.baseBenefitPoolDurationMonths;
  const eobMonths = terms.extensionOfBenefitsPoolDurationMonths;
  if (accelMonths === undefined && eobMonths === undefined) return null;

  const dailyByEnvironment = terms.nominalDailyBenefitByEnvironment ?? {};
  const baseMonthly =
    (dailyByEnvironment[month.context.careEnvironment] ?? terms.nominalDailyBenefitBase) *
    DAYS_IN_MONTH;
  const intYear = Math.floor(month.context.policyYear);
  // Matches the engine's `compoundFactor`: exponent = policyYear − 1, capped
  // at the pool's own ACIO cap years.
  const capFor = (rate: number, capYears: number): number => {
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = capYears > 0 ? Math.min(rawIncrements, capYears) : rawIncrements;
    return baseMonthly * (1 + rate) ** increments;
  };
  const fromEob = month.facts.appliedCapPoolSource === 'eob';
  const appliedCap = month.facts.appliedMonthlyBenefitCap;
  const accelCap = fromEob
    ? capFor(
        terms.baseBenefitPoolInflationRate ?? terms.inflationRate,
        terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears,
      )
    : appliedCap;
  const eobCap = fromEob
    ? appliedCap
    : capFor(
        terms.extensionOfBenefitsPoolInflationRate ?? terms.inflationRate,
        terms.extensionOfBenefitsPoolInflationCapYears ?? terms.inflationCapYears,
      );
  return (
    <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-[10px]">
      <span className="col-span-2 text-gray-300">Pool ceiling at each pool's monthly cap</span>
      {accelMonths !== undefined && (
        <>
          <span className="text-gray-300">Acceleration × {accelMonths} mo</span>
          <span className="font-semibold tabular-nums">{currency(accelCap * accelMonths)}</span>
        </>
      )}
      {eobMonths !== undefined && eobMonths > 0 && (
        <>
          <span className="text-gray-300">EOB × {eobMonths} mo</span>
          <span className="font-semibold tabular-nums">{currency(eobCap * eobMonths)}</span>
        </>
      )}
    </div>
  );
}

export const nylAssetFlexPresentation: StrategyPresentation = {
  accumulationColumns: buildAssetFlexAccumulationColumns,

  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    // Headline = the engine's own applied cap for this row; the tooltip's
    // base × (1+r)^n derivation is reconstructed from terms for display only.
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears, fromEob } = appliedCapTooltipInputs(month, terms);

    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    // Exponent = policyYear − 1, capped at capYears, matching the engine's
    // `compoundFactor` so the tooltip's base × (1+r)^n equals the displayed result.
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    const poolLabel = fromEob ? 'EOB' : 'Acceleration';

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear} · ${poolLabel} monthly limit`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `ACIO capped at year ${cap}` : undefined}
          compoundLabel="Compound ACIO growth"
          footer={poolCeilingFooter(month, terms)}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  poolCellTooltip: ({ month, openState, terms }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    const eobUntouched = openEob > 0 && closeEob === openEob && eobAccrual === 0;

    const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
    const policyYear = month.context.policyYear;
    const baseAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.baseBenefitPoolDurationMonths,
      terms.baseBenefitPoolAtIssue,
    );
    const eobAtIssueNote = poolAtIssueNote(
      baseMonthly,
      terms.extensionOfBenefitsPoolDurationMonths,
      terms.extensionOfBenefitsPoolAtIssue,
    );
    const eobNoteParts = [eobAtIssueNote, eobUntouched ? 'Not yet engaged' : undefined].filter(
      Boolean,
    );

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Asset Flex pays from the Acceleration Benefit first; the Extension of Benefits pool takes over once Acceleration is exhausted. ACIO grows both pools at the policy anniversary."
        sections={[
          {
            heading: `Acceleration Benefit · Policy year ${policyYear}`,
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'ACIO accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
            note: baseAtIssueNote,
          },
          {
            heading: `Extension of Benefits · Policy year ${policyYear}`,
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'ACIO accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
            note: eobNoteParts.length > 0 ? eobNoteParts.join(' · ') : undefined,
          },
        ]}
        footer="Pool dollars are tracked through reimbursement and inflated by ACIO each anniversary. May diverge slightly from the carrier illustration's printed forward-inflated pool totals."
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const accelDrawdown = month.deltas.base.depletion;
    if (open === 0 && close === 0 && reduction === 0) return null;

    // When the Residual floor capped the reduction, the raw Acceleration
    // drawdown is bigger than the actual DB depletion. Surface that gap so
    // the advisor sees why Opening − Drawn ≠ Closing in this row.
    const floorNote = flooredAtMinimum
      ? `Acceleration drew ${currency(accelDrawdown)}; depletion capped at the Residual Death Benefit (${currency(close)}).`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, reduced in direct proportion to the remaining Acceleration Benefit pool as it is drawn down. EOB drawdown does NOT deplete the death benefit. Floors at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              {
                label: 'Reduced this month',
                value: reduction,
                kind: 'subtract',
              },
            ],
            note: floorNote,
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const cost = month.facts.effectiveCareCost;
    const eligibleDays = month.facts.coverageEligibleDays;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    if (eligibleDays === 0) return null;

    // Reimbursement is computed against pro-rated cost (eligibleDays ×
    // dailyCareCost), not the full-month cost. Surface that distinction so
    // partial-eligibility months don't mislabel the cost basis or
    // overstate OOP attribution to "policy didn't cover".
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
    const ineligibleDayCost = Math.max(cost - eligibleCost, 0);
    const eligibleOop = Math.max(eligibleCost - paid, 0);

    let rule: string;
    if (paid === 0 && closeBase === 0 && closeEob === 0) {
      rule =
        'Both LTC pools are exhausted — the policy reimburses nothing; the full care cost is out-of-pocket.';
    } else if (paid >= eligibleCost - 0.01) {
      rule =
        'Care cost is at or below the monthly benefit cap. Since Asset Flex is a reimbursement product, the policy pays the actual eligible cost.';
    } else if (paid > 0) {
      rule =
        'Care cost exceeds the monthly benefit cap. Asset Flex reimburses up to the cap; the excess is out-of-pocket.';
    } else {
      rule = 'No reimbursement this month.';
    }

    const drawnFrom: string[] = [];
    if (drawnBase > 0) drawnFrom.push(`Acceleration ${currency(drawnBase)}`);
    if (drawnEob > 0) drawnFrom.push(`Extension of Benefits ${currency(drawnEob)}`);

    const proratedNote =
      isPartial && paid > 0
        ? `Pro-rated for ${eligibleDays} eligible day${eligibleDays === 1 ? '' : 's'} (waiting period or <2 ADL consumed the rest of the month).`
        : null;

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — reimbursement</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Care cost this month</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          {isPartial && (
            <>
              <span className="text-gray-300">Eligible portion</span>
              <span className="font-semibold tabular-nums">
                {currency(eligibleCost)}
                <span className="ml-1 font-normal text-gray-400">
                  ({eligibleDays}d × {currency(dailyCost)})
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">Reimbursed</span>
          <span className="font-semibold tabular-nums">{currency(paid)}</span>
          {eligibleOop > 0 && (
            <>
              <span className="text-gray-300">
                {isPartial ? 'OOP (over cap)' : 'Out-of-pocket'}
              </span>
              <span className="font-semibold text-rose-300 tabular-nums">
                {currency(eligibleOop)}
              </span>
            </>
          )}
          {ineligibleDayCost > 0 && (
            <>
              <span className="text-gray-300">OOP (ineligible days)</span>
              <span className="font-semibold text-rose-300 tabular-nums">
                {currency(ineligibleDayCost)}
              </span>
            </>
          )}
        </div>
        {drawnFrom.length > 0 && (
          <div className="mt-1 text-[10px] text-gray-400">Drawn from: {drawnFrom.join(' + ')}</div>
        )}
        {proratedNote && <div className="mt-1 text-[10px] text-gray-400">{proratedNote}</div>}
      </TooltipContent>
    );
  },

  // Asset Flex's monthly cap inflates by ACIO on the On-Face amount
  // (`baseBenefitPoolInflationRate`), which can differ from the policy-wide
  // ACIO when an On-Face override is set. Use that rate in the tooltip so
  // the `base × (1+r)^n` math reconciles with the displayed cap.
  benefitInflationRate: terms => terms.baseBenefitPoolInflationRate ?? terms.inflationRate,

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance. Asset Flex draws from the Acceleration Benefit (AOB) first, then the Extension of Benefits (EOB) pool. ACIO grows both pools at each policy anniversary."
        formulas={[
          {
            label: 'Closing pool',
            expression: '(opening AOB + opening EOB) + ACIO accrual − drawn this month',
          },
          { label: 'Draw order', expression: 'AOB first; EOB engages once AOB is exhausted' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Reimbursement payout this month, with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Monthly payout',
            expression: 'min(eligible cost, monthly benefit cap, pool remaining)',
          },
          {
            label: 'Eligible cost',
            expression: 'daily care cost × eligible days (waiting period / <2 ADL excluded)',
          },
        ]}
        notes="Partial-eligibility months are pro-rated by eligible days; OOP can come from over-cap or ineligible days."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum (cap) for this policy year, with the daily equivalent beneath. Asset Flex inflates the cap by On-Face ACIO."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue monthly ×{' '}
                <GrowthFactor
                  rate="ACIO rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          { label: 'Pool ceiling', expression: 'monthly cap × duration months (AOB or EOB)' },
        ]}
        notes="ACIO capped at the configured cap year if specified. On-Face ACIO can differ from policy-wide ACIO."
      />
    ),
    poolLimitTooltip: (
      <HeaderTooltip
        description="Inflated AOB + EOB pool sizes at this policy year if no benefits were used. Pools keep growing on each future anniversary until any ACIO cap is reached. Distinct from Pool remaining, which is the actual balance after payouts."
        formulas={[
          {
            label: 'AOB',
            expression: (
              <>
                AOB at issue ×{' '}
                <GrowthFactor
                  rate="ACIO rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          {
            label: 'EOB',
            expression: (
              <>
                EOB at issue ×{' '}
                <GrowthFactor
                  rate="ACIO rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
          { label: 'Total', expression: 'AOB value + EOB value' },
        ]}
        notes="Each pool capped at its own ACIO cap year if specified — once capped, the value is a true ceiling."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the AOB + EOB pools through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (AOB depletion + EOB depletion)' },
          {
            label: 'Identity',
            expression: 'Pool limit − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Asset Flex reduces the death benefit in direct proportion to the remaining Acceleration pool as it is drawn down; EOB drawdown does not affect it."
        formulas={[
          {
            label: 'Death benefit',
            expression: 'open DB × (AOB pool after drawdown ÷ AOB pool before drawdown)',
          },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound ACIO growth',
    benefitInflationCapNote: capYear => `ACIO capped at year ${capYear}`,
  },
};
